import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

(jsPDF as any).autoTable = autoTable;

type DocKind = 'portal' | 'app';

const MARGIN = 16;
const PAGE_BOTTOM = 280;
const LINE = 5.2;

const stripMdInline = (text: string) =>
  text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\((.+?)\)/g, '$1')
    .replace(/^>\s?/, '')
    .trim();

const ensureSpace = (doc: jsPDF, y: number, needed = LINE * 2): number => {
  if (y + needed <= PAGE_BOTTOM) return y;
  doc.addPage();
  return MARGIN + 4;
};

const writeWrapped = (doc: jsPDF, text: string, x: number, y: number, maxWidth: number, fontSize = 10): number => {
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(text, maxWidth) as string[];
  for (const line of lines) {
    y = ensureSpace(doc, y, LINE);
    doc.text(line, x, y);
    y += LINE;
  }
  return y;
};

export const downloadDocumentationPdf = (markdown: string, kind: DocKind): void => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN * 2;
  const title = kind === 'portal' ? 'ASMonitor Portal — Benutzerdokumentation' : 'ASMonitor App — Benutzerdokumentation';

  // Cover header
  doc.setFillColor(204, 0, 0);
  doc.rect(0, 0, pageWidth, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(title, MARGIN, 17);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Stand: ${new Date().toLocaleDateString('de-CH')}`, MARGIN, 24);

  let y = 38;
  doc.setTextColor(26, 26, 26);

  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  let inTable = false;
  let tableHeaders: string[] = [];
  let tableRows: string[][] = [];

  const flushTable = () => {
    if (!tableHeaders.length) {
      inTable = false;
      tableRows = [];
      return;
    }
    y = ensureSpace(doc, y, 24);
    autoTable(doc, {
      startY: y,
      head: [tableHeaders],
      body: tableRows,
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 8.5, cellPadding: 2.2, textColor: [40, 40, 40] },
      headStyles: { fillColor: [204, 0, 0], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [250, 250, 250] },
    });
    y = ((doc as any).lastAutoTable?.finalY || y) + 8;
    inTable = false;
    tableHeaders = [];
    tableRows = [];
  };

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trimEnd();
    i += 1;

    if (!line.trim()) {
      if (inTable) flushTable();
      y += 2;
      continue;
    }

    if (/^---+$/.test(line.trim())) {
      if (inTable) flushTable();
      y = ensureSpace(doc, y, 8);
      doc.setDrawColor(220, 220, 220);
      doc.line(MARGIN, y, pageWidth - MARGIN, y);
      y += 6;
      continue;
    }

    // Table rows
    if (line.includes('|') && line.trim().startsWith('|')) {
      const cells = line
        .split('|')
        .map((c) => stripMdInline(c))
        .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);

      const isSeparator = cells.every((c) => /^:?-+:?$/.test(c.replace(/\s/g, '')));
      if (isSeparator) {
        inTable = true;
        continue;
      }

      if (!inTable && tableHeaders.length === 0) {
        tableHeaders = cells;
        inTable = true;
        continue;
      }

      if (inTable) {
        tableRows.push(cells);
        continue;
      }
    } else if (inTable) {
      flushTable();
    }

    if (line.startsWith('# ')) {
      y = ensureSpace(doc, y, 14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(204, 0, 0);
      y = writeWrapped(doc, stripMdInline(line.slice(2)), MARGIN, y, contentWidth, 16);
      doc.setTextColor(26, 26, 26);
      y += 3;
      continue;
    }

    if (line.startsWith('## ')) {
      y = ensureSpace(doc, y, 12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(204, 0, 0);
      y = writeWrapped(doc, stripMdInline(line.slice(3)), MARGIN, y, contentWidth, 13);
      doc.setTextColor(26, 26, 26);
      y += 2;
      continue;
    }

    if (line.startsWith('### ')) {
      y = ensureSpace(doc, y, 10);
      doc.setFont('helvetica', 'bold');
      y = writeWrapped(doc, stripMdInline(line.slice(4)), MARGIN, y, contentWidth, 11);
      y += 1;
      continue;
    }

    if (line.startsWith('> ')) {
      y = ensureSpace(doc, y, 12);
      doc.setFillColor(255, 245, 245);
      doc.setDrawColor(204, 0, 0);
      const note = stripMdInline(line.replace(/^>\s?/, ''));
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const noteLines = doc.splitTextToSize(note, contentWidth - 8) as string[];
      const boxH = noteLines.length * 4.4 + 6;
      doc.roundedRect(MARGIN, y - 3.5, contentWidth, boxH, 2, 2, 'FD');
      doc.setTextColor(90, 16, 16);
      let ny = y;
      for (const nl of noteLines) {
        doc.text(nl, MARGIN + 4, ny);
        ny += 4.4;
      }
      doc.setTextColor(26, 26, 26);
      y = y - 3.5 + boxH + 4;
      continue;
    }

    if (/^[-*]\s+/.test(line.trim())) {
      const item = stripMdInline(line.trim().replace(/^[-*]\s+/, ''));
      doc.setFont('helvetica', 'normal');
      y = ensureSpace(doc, y, LINE);
      doc.setFontSize(10);
      doc.text('•', MARGIN + 1, y);
      y = writeWrapped(doc, item, MARGIN + 7, y, contentWidth - 7, 10);
      continue;
    }

    if (/^\d+\.\s+/.test(line.trim())) {
      const match = /^(\d+)\.\s+(.*)$/.exec(line.trim());
      if (match) {
        const item = stripMdInline(match[2]);
        doc.setFont('helvetica', 'normal');
        y = ensureSpace(doc, y, LINE);
        doc.setFontSize(10);
        doc.text(`${match[1]}.`, MARGIN + 1, y);
        y = writeWrapped(doc, item, MARGIN + 9, y, contentWidth - 9, 10);
        continue;
      }
    }

    doc.setFont('helvetica', 'normal');
    y = writeWrapped(doc, stripMdInline(line), MARGIN, y, contentWidth, 10);
    y += 1;
  }

  if (inTable) flushTable();

  // Footer page numbers
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`ASMonitor · Seite ${p} / ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
  }

  const filename =
    kind === 'portal'
      ? 'ASMonitor_Portal_Benutzerdokumentation.pdf'
      : 'ASMonitor_App_Benutzerdokumentation.pdf';
  doc.save(filename);
};
